// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";
import { Result, Ok, Err } from "@swiggy-private/js-fp";
import { NREvent } from "@swiggy-private/blaze.js/lib/types";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";

// internal dep
import {
    recordNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    NEWRELIC_TABLES,
    isPopFlow,
    fetchData,
} from "@swiggy-private/checkout-core";

// helpers
import { isEmpty } from "@swiggy-private/core-ui";
export interface ApiCallParams {
    apiHost: string;
    endpoint: string;
    searchParams?: { [key: string]: string };
    bodyParams?: { [key: string]: unknown };
    options?: RequestInit;
    timeout?: number;
    retry?: boolean;
    retryCount?: number;
    shouldRedirectToMarketplace?: boolean;
    excludeDefaultHeaders?: boolean;
    onResponseCb?: (response: unknown) => Record<string, unknown>;
}

const DOWNSTREAM_TRIGGER_SOURCE = {
    API_SERVER: "api-server",
    WEB_APP: "web-app",
};

function recordSessionData<T>(response: ApiResponse<T>, url: string): void {
    const existingSid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "";
    const existingTid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.tid) || "";

    // If response object is null, no need to update session data.
    if (!response) {
        return;
    }

    const { tid, sid } = response;

    if (!isEmpty(tid)) {
        // record NR event in case of change in tid
        if (existingTid !== tid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.tid, tid);
    }

    if (!isEmpty(sid)) {
        // record NR event in case of change in sid
        if (existingSid !== sid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
                sid,
                existingSid,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.sid, sid);
    }
}

async function nrInterceptor<T>(
    request: { startTime: number; url: string; opts: RequestInit; responseSize?: number },
    response: ApiResponse<T>,
    apiRetriedCount?: number,
): Promise<ApiResponse<T>> {
    // logging ctaText and ctaAction only for snacc app and we need to cleanup this later once we have debugging done in prod
    const isPopCart = isPopFlow();
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        statusCode: response.statusCode ?? response.failure?.errorCode ?? -1,
        statusMessage:
            response.statusMessage ?? response.failure?.errorMessage ?? response.success?.statusMessage ?? "",
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        ctaText: isPopCart ? response?.ctaText || "" : "",
        ctaAction: isPopCart ? response?.ctaAction || "" : "",
        hasData: `${!isEmpty(response?.data || response?.success?.cards)}`,
        apiRetriedCount: apiRetriedCount ?? 0,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
    });

    //add logic for updating tid and sid
    recordSessionData(response, request.url);

    return response;
}

async function nrDownstreamInterceptor<T>(
    request: { startTime: number; url: string; opts: RequestInit; responseSize?: number },
    response: T,
    apiRetriedCount?: number,
): Promise<T> {
    const typedResponse = response as T & ApiResponse<T>;
    let event: NREvent = {
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        apiRetriedCount: apiRetriedCount ?? 0,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
    };

    if (response instanceof Object && !isEmpty(response)) {
        event = {
            ...event,
            statusCode: typedResponse.statusCode ?? typedResponse.failure?.errorCode ?? "--",
            statusMessage:
                typedResponse.statusMessage ??
                typedResponse.failure?.errorMessage ??
                typedResponse.success?.statusMessage ??
                "-",
            hasData: `${!isEmpty(typedResponse?.data || typedResponse?.success?.cards)}`,
        };
    }
    recordNREvent(event);

    return response;
}

export async function makeApiCall<T>(params: ApiCallParams): Promise<ApiResponse<T>> {
    const { response, startTime, logUrl, options, apiRetriedCount, responseSize } = await fetchData<ApiResponse<T>>(
        params,
        DOWNSTREAM_TRIGGER_SOURCE.API_SERVER,
    );
    return nrInterceptor({ startTime, url: logUrl, opts: options, responseSize }, response, apiRetriedCount);
}

/**
 * @deprecated Use makeDownstreamApiCall instead from checkout-core. This function will be removed in a future
 */
export async function makeDownstreamApiCall<T>({ onResponseCb, ...params }: ApiCallParams): Promise<T> {
    const { response, startTime, logUrl, options, apiRetriedCount, responseSize } = await fetchData<T>(
        params,
        DOWNSTREAM_TRIGGER_SOURCE.WEB_APP,
    );
    try {
        onResponseCb?.(response);
    } catch (err) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_ERROR,
            url: logUrl,
            error: err instanceof Error ? err.name : JSON.stringify(err),
            errorMessage: err instanceof Error ? err.message : JSON.stringify(err),
        });
    }
    return nrDownstreamInterceptor({ startTime, url: logUrl, opts: options, responseSize }, response, apiRetriedCount);
}

export function unwrapResponse<T>(response?: ApiResponse<T>): Result<T, string> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err(response?.statusMessage || OOPS_SOMETHING_WENT_WRONG);
}

export function unwrapResponseWithCode<T>(
    response?: ApiResponse<T>,
): Result<T, Pick<ApiResponse, "statusCode" | "statusMessage">> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err({
        statusCode: response?.statusCode || 1,
        statusMessage: response?.statusMessage || OOPS_SOMETHING_WENT_WRONG,
    });
}
